import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db, auth } from "../../config/firebase"; // Adjust the import path as necessary
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  updateDoc,
  arrayUnion,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Snackbar,
  Alert,
  Box,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import "../../styles/ClassHome.css"; // Adjust the import path as necessary

export default function ClassHome() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [collegeId, setCollegeId] = useState(null);
  const [joinedClass, setJoinedClass] = useState(null);
  const [classrooms, setClassrooms] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        const userData = {
          email: currentUser.email,
          uid: currentUser.uid,
        };
        setUser(userData);

        // Determine user's college and role
        const collegesSnapshot = await getDocs(collection(db, "colleges"));
        for (const collegeDoc of collegesSnapshot.docs) {
          const collegeRef = collegeDoc.ref;
          const teachersRef = collection(collegeRef, "teachers");
          const studentsRef = collection(collegeRef, "students");

          const teacherQuery = query(teachersRef, where("email", "==", userData.email));
          const studentQuery = query(studentsRef, where("email", "==", userData.email));
          
          const teacherSnapshot = await getDocs(teacherQuery);
          const studentSnapshot = await getDocs(studentQuery);

          if (!teacherSnapshot.empty) {
            setUserRole("teacher");
            setCollegeId(collegeDoc.id);
            break;
          }
          if (!studentSnapshot.empty) {
            setUserRole("student");
            setCollegeId(collegeDoc.id);
            break;
          }
        }
      } else {
        setUser(null);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (collegeId) {
      const fetchClasses = async () => {
        const classData = [];
        const querySnapshot = await getDocs(collection(db, `colleges/${collegeId}/courses`));
        querySnapshot.forEach((doc) => {
          classData.push({ id: doc.id, ...doc.data() });
        });
        setClassrooms(classData);
      };
      fetchClasses();
    }
  }, [collegeId]);

  const handleJoin = async (classItem) => {
    if (!user || userRole !== "student") {
      setMessage("Only students can join a class.");
      setOpen(true);
      return;
    }

    if (joinedClass) {
      setMessage(`You have already joined ${joinedClass}.`);
      setOpen(true);
      return;
    }

    try {
      const classDoc = doc(db, `colleges/${collegeId}/courses`, classItem.id);
      await updateDoc(classDoc, {
        studentIds: arrayUnion(user.uid),
      });

      setMessage(`Joined class: ${classItem.title}`);
      setOpen(true);
      setJoinedClass(classItem.title);
      navigate("/classpage", {
        state: { className: classItem.title, classroomId: classItem.id },
      });
    } catch (error) {
      console.error("Error joining class:", error);
      setMessage("Failed to join class. Please try again.");
      setOpen(true);
    }
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  return (
    <Box className="class-home">
      <Typography variant="h4" className="class-title">
        Your Classes
      </Typography>
      <Grid container spacing={3}>
        {classrooms.map((classItem) => (
          <Grid item xs={12} sm={6} md={4} key={classItem.id}>
            <Card className="class-card">
              <CardContent>
                <Typography variant="h6">{classItem.title}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {classItem.description}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Instructor: {classItem.teacherId || "N/A"}
                </Typography>
              </CardContent>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={() => handleJoin(classItem)}
                disabled={Boolean(joinedClass) || userRole === "teacher"}
              >
                Join Class
              </Button>
            </Card>
          </Grid>
        ))}
        <Grid item xs={12} sm={6} md={4}>
          <Card className="add-class-card">
            <Button fullWidth className="add-class-btn">
              <AddIcon fontSize="large" />
              <Typography variant="body1">Join or Create Class</Typography>
            </Button>
          </Card>
        </Grid>
      </Grid>
      <Snackbar open={open} autoHideDuration={6000} onClose={handleClose}>
        <Alert onClose={handleClose} severity="success" sx={{ width: "100%" }}>
          {message}
        </Alert>
      </Snackbar>
    </Box>
  );
}